// Core imports
import React, { Fragment, useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
import { t } from 'i18next';
import { useLocation } from 'react-router-dom';

// Component Imports
import { Card, Col, Row } from 'antd';
import InterviewsUsageSection from './InterviewsUsageSection';
import InterviewsSuccessfulVsFailedSection from './InterviewsSuccessfulVsFailedSection';

// Misc imports
import { ANALYTICS_SECTIONS } from '../../constants/main';
import { useFetchInterviewsAnalyticsQuery } from '../../store/analytics/analyticsApi';
import { isEmptyArray } from '../../utils/is/isEmptyArray';

const AnalyticsInterviewsTab = ({
  requestParams,
  isPublicMode,
  setIsLoading = () => false
}) => {
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);

  const { data: interviewsAnalytics, isFetching: isFetchingInterviewsAnalytics, isUninitialized } = useFetchInterviewsAnalyticsQuery({ ...requestParams?.[0] }, { skip: isEmptyArray(requestParams) });

  // Memoize loading state to prevent unnecessary re-renders
  const isLoading = useMemo(() =>
    isFetchingInterviewsAnalytics || isUninitialized,
  [isFetchingInterviewsAnalytics, isUninitialized]
  );

  useEffect(() => {
    setIsLoading(isLoading);

    return () => {
      setIsLoading(false);
    };
  }, [isLoading]);

  const renderInterviewsSection = () => {
    // If token in the url, then we're in public mode
    const isPublicModeFromUrl = searchParams.has('token');

    return (
      <Fragment>
        <Row gutter={[16, 16]} className="mb-5 hide-empty" id={`analytics-section_${ANALYTICS_SECTIONS.INTERVIEWS.key}`}>
          <Col xs={24} md={12} xxl={isPublicModeFromUrl ? 12 : 8} id={`analytics-section_${ANALYTICS_SECTIONS.INTERVIEWS.key}_${ANALYTICS_SECTIONS.INTERVIEWS.subSections.USAGE.key}`}>
            <Card className="h-100">
              <InterviewsUsageSection
                data={interviewsAnalytics?.usage}
                isLoading={isLoading}
                sectionTitle={t('Usage')}
              />
            </Card>
          </Col>

          <Col xs={24} md={12} xxl={isPublicModeFromUrl ? 12 : 8} id={`analytics-section_${ANALYTICS_SECTIONS.INTERVIEWS.key}_${ANALYTICS_SECTIONS.INTERVIEWS.subSections.SUCCESSFUL_VS_FAILED.key}`}>
            <Card className="h-100">
              <InterviewsSuccessfulVsFailedSection
                data={interviewsAnalytics?.outcome}
                isLoading={isLoading}
                sectionTitle={t('Successful vs Failed')}
              />
            </Card>
          </Col>
        </Row>
      </Fragment>
    );
  };

  return (
    <Fragment>
      {renderInterviewsSection()}
    </Fragment>
  );
};

AnalyticsInterviewsTab.propTypes = {
  requestParams: PropTypes.array,
  isPublicMode: PropTypes.bool,
  setIsLoading: PropTypes.func
};

export default AnalyticsInterviewsTab;
